import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};

interface BankInfo {
  bank_name: string;
  account_number: string;
  account_holder: string;
}

interface RedeemDialogProps {
  open: boolean;
  onClose: () => void;
  redeemAmount: number;
  setRedeemAmount: (v: number) => void;
  bankInfo: BankInfo;
  setBankInfo: (info: BankInfo) => void;
  onConfirm: () => void; // async confirm handler
}

const MIN_REDEEM_AMOUNT = 100; // Example minimum
const CHIP_TO_USD = 0.1;

const RedeemDialog: React.FC<RedeemDialogProps> = ({
  open,
  onClose,
  redeemAmount,
  setRedeemAmount,
  bankInfo,
  setBankInfo,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    amount: "",
    bank_name: "",
    account_number: "",
    account_holder: "",
  });

  const validate = () => {
    const newErrors = { ...errors };
    newErrors.amount =
      redeemAmount < MIN_REDEEM_AMOUNT
        ? `Minimum redeem amount is ${MIN_REDEEM_AMOUNT} chips`
        : "";

    newErrors.bank_name = bankInfo.bank_name ? "" : "Bank name is required";
    newErrors.account_number = bankInfo.account_number
      ? ""
      : "Account number is required";
    newErrors.account_holder = bankInfo.account_holder
      ? ""
      : "Account holder name is required";

    setErrors(newErrors);
    return !Object.values(newErrors).some((err) => err !== "");
  };

  const handleConfirm = async () => {
    if (!validate()) return;
    setLoading(true);
    await onConfirm();
    setLoading(false);
    onClose();
  };

  const isDisabled =
    loading ||
    redeemAmount < MIN_REDEEM_AMOUNT ||
    !bankInfo.bank_name ||
    !bankInfo.account_number ||
    !bankInfo.account_holder;

  const usdEquivalent = (redeemAmount * CHIP_TO_USD).toFixed(2);

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          padding: "8px 0",
          fontFamily: "'Lexend', sans-serif",
          backgroundColor: COLORS.panel,
          border: `1px solid ${COLORS.border}`,
          color: COLORS.text,
        },
      }}
    >
      {/* Title */}
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.25rem",
          textAlign: "center",
          color: COLORS.text,
        }}
      >
        Redeem Chips
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ px: 4, py: 2 }}>
        <Typography
          variant="body2"
          sx={{ mb: 2, color: COLORS.muted, textAlign: "center" }}
        >
          Enter amount and bank details to redeem your earnings
        </Typography>

        {/* Amount Field */}
        <TextField
          fullWidth
          label="Redeem Amount"
          type="number"
          value={redeemAmount}
          onChange={(e) => setRedeemAmount(Number(e.target.value))}
          error={!!errors.amount}
          helperText={errors.amount}
          InputProps={{ inputProps: { min: MIN_REDEEM_AMOUNT } }}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: COLORS.text,
              "& fieldset": {
                borderColor: COLORS.border,
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.red,
              },
            },
            "& .MuiInputLabel-root": {
              color: COLORS.muted,
            },
            "& .MuiInputBase-input": {
              color: COLORS.text,
            },
            "& .MuiFormHelperText-root": {
              color: COLORS.muted,
            },
          }}
        />

        {/* Preview USD */}
        {redeemAmount >= MIN_REDEEM_AMOUNT && (
          <Box mt={1} mb={2} textAlign="center">
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              ≈ ${usdEquivalent} USD will be transferred
            </Typography>
          </Box>
        )}

        {/* Bank Fields */}
        <TextField
          fullWidth
          label="Bank Name"
          value={bankInfo.bank_name}
          onChange={(e) =>
            setBankInfo({ ...bankInfo, bank_name: e.target.value })
          }
          error={!!errors.bank_name}
          helperText={errors.bank_name}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: COLORS.text,
              "& fieldset": {
                borderColor: COLORS.border,
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.red,
              },
            },
            "& .MuiInputLabel-root": {
              color: COLORS.muted,
            },
            "& .MuiInputBase-input": {
              color: COLORS.text,
            },
            "& .MuiFormHelperText-root": {
              color: COLORS.muted,
            },
          }}
        />
        <TextField
          fullWidth
          label="Account Number"
          value={bankInfo.account_number}
          onChange={(e) =>
            setBankInfo({ ...bankInfo, account_number: e.target.value })
          }
          error={!!errors.account_number}
          helperText={errors.account_number}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: COLORS.text,
              "& fieldset": {
                borderColor: COLORS.border,
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.red,
              },
            },
            "& .MuiInputLabel-root": {
              color: COLORS.muted,
            },
            "& .MuiInputBase-input": {
              color: COLORS.text,
            },
            "& .MuiFormHelperText-root": {
              color: COLORS.muted,
            },
          }}
        />
        <TextField
          fullWidth
          label="Account Holder"
          value={bankInfo.account_holder}
          onChange={(e) =>
            setBankInfo({ ...bankInfo, account_holder: e.target.value })
          }
          error={!!errors.account_holder}
          helperText={errors.account_holder}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: COLORS.text,
              "& fieldset": {
                borderColor: COLORS.border,
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.red,
              },
            },
            "& .MuiInputLabel-root": {
              color: COLORS.muted,
            },
            "& .MuiInputBase-input": {
              color: COLORS.text,
            },
            "& .MuiFormHelperText-root": {
              color: COLORS.muted,
            },
          }}
        />

        <Box mt={2} textAlign="center">
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            Bank details will be verified before processing redemption
          </Typography>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          pb: 3,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontWeight: 700,
            textTransform: "none",
            color: COLORS.muted,
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isDisabled}
          sx={{
            background: COLORS.red,
            color: COLORS.text,
            fontWeight: 700,
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(229,9,20,0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              background: COLORS.redHover,
              boxShadow: "0 6px 14px rgba(229,9,20,0.4)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RedeemDialog;
